import { Link } from "react-router-dom";
import { useAuth } from "../App";
import { 
  Calendar, 
  Users, 
  MessageSquare, 
  FileImage, 
  DollarSign, 
  BarChart3, 
  Shield, 
  Clock, 
  Sparkles,
  ChevronRight,
  Check
} from "lucide-react";
import { Button } from "@/components/ui/button";

export default function LandingPage() {
  const { user } = useAuth();

  const features = [
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Agendamento Inteligente",
      description: "Sistema de agendamento online com lembretes automáticos e gestão de horários."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Prontuário Eletrônico",
      description: "Histórico completo dos pacientes com todos os registros médicos em um só lugar."
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Chat IA para Triagem",
      description: "Assistente virtual que ajuda na triagem de sintomas e orientações iniciais."
    },
    {
      icon: <FileImage className="w-6 h-6" />,
      title: "Análise de Imagens",
      description: "IA que auxilia na análise de radiografias e imagens dentárias."
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: "Gestão Financeira",
      description: "Controle de orçamentos, pagamentos e fluxo de caixa da clínica."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Relatórios Automáticos",
      description: "Geração automática de relatórios com insights sobre a clínica."
    }
  ];

  const benefits = [
    "Reduza tempo administrativo em até 60%",
    "Aumente a satisfação dos pacientes",
    "Tome decisões baseadas em dados",
    "Nunca perca um agendamento",
    "Segurança e conformidade LGPD"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                <span className="text-white font-bold text-lg">L</span>
              </div>
              <span className="font-bold text-xl text-slate-800">ItapeviDents</span>
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-teal-700 transition-colors">Recursos</a>
              <a href="#benefits" className="text-slate-600 hover:text-teal-700 transition-colors">Benefícios</a>
              <a href="#pricing" className="text-slate-600 hover:text-teal-700 transition-colors">Preços</a>
            </nav>

            <div className="flex items-center gap-3">
              {!user && (
                <>
                  <Link to="/login">
                    <Button data-testid="login-btn" variant="ghost" className="text-slate-600 hover:text-teal-700">
                      Entrar
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button data-testid="register-btn" className="bg-teal-700 hover:bg-teal-800 text-white rounded-full px-6">
                      Começar Grátis
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeIn">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 text-teal-800 rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                Potencializado por IA
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6">
                Gestão odontológica{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-blue-500">inteligente</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-lg">
                Transforme sua clínica com IA. Agendamentos, prontuários, triagem e análise de imagens 
                em uma única plataforma moderna e intuitiva.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button data-testid="hero-cta-btn" size="lg" className="bg-teal-700 hover:bg-teal-800 text-white rounded-full px-8 h-12 shadow-lg hover:shadow-xl transition-all">
                    Começar Gratuitamente
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Button data-testid="hero-demo-btn" variant="outline" size="lg" className="rounded-full px-8 h-12 border-slate-300 hover:border-teal-500 hover:text-teal-700">
                  Ver Demonstração
                </Button>
              </div>
              <div className="flex items-center gap-6 mt-8 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-teal-600" />
                  Seguro e LGPD
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-teal-600" />
                  Setup em 5 min
                </div>
              </div>
            </div>
            
            <div className="relative animate-fadeIn" style={{ animationDelay: "0.2s" }}>
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.pexels.com/photos/7422520/pexels-photo-7422520.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Clínica Dental Moderna"
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-teal-200 rounded-full blur-3xl opacity-50"></div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-blue-200 rounded-full blur-3xl opacity-50"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Tudo que sua clínica precisa
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Ferramentas poderosas integradas para otimizar cada aspecto da sua clínica odontológica.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-all group cursor-pointer card-hover animate-fadeIn"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center mb-4 group-hover:bg-teal-700 group-hover:text-white transition-colors">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 px-4 bg-slate-900">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                Por que escolher a ItapeviDents?
              </h2>
              <p className="text-slate-400 text-lg mb-8">
                Nossa plataforma foi desenvolvida por dentistas, para dentistas. 
                Entendemos os desafios do dia a dia da sua clínica.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3 text-white">
                    <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/305567/pexels-photo-305567.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Interior da Clínica"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
            Pronto para transformar sua clínica?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Comece gratuitamente hoje e veja a diferença que a tecnologia pode fazer.
          </p>
          <Link to="/register">
            <Button data-testid="cta-final-btn" size="lg" className="bg-teal-700 hover:bg-teal-800 text-white rounded-full px-10 h-14 text-lg shadow-lg">
              Criar Conta Gratuita
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-slate-200">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                <span className="text-white font-bold text-lg">L</span>
              </div>
              <span className="font-bold text-xl text-slate-800">ItapeviDents</span>
            </div>
            <p className="text-slate-500 text-sm">
              © 2024 ItapeviDents. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
